import React from 'react';

import {
	BottomCard,
	BottomCardAction,
	BottomCardForm,
} from '@/components/shared/BottomCard';
import { AsaphButton } from '@/components/shared/Button';
import { Label } from '@/components/shared/Inputs/Label';

import { BiblePassageInput } from '../BiblePassageInput/BiblePassageInput';
import { useGetBiblePassageAnalysisMutation } from '@/graphql';
import { FetchError } from '@/graphql/codegen-fetcher';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { FormHelperText } from '@material-ui/core';

interface PassageChipPanelProps {
	initialBibleReference?: string;
	onSave: (bibleReference: string | undefined) => void;
	onClose: () => void;
}

export const PassageChipPanel: React.FC<PassageChipPanelProps> = ({
	initialBibleReference,
	onSave,
	onClose,
}) => {
	const [bibleReference, setBibleReference] = React.useState<string>(
		initialBibleReference ?? '',
	);
	const {
		isLoading: isBiblePassageLoading,
		mutateAsync: getBiblePassage,
		error: biblePassageError,
		isError: isBiblePassageError,
		reset: resetBiblePassage,
	} = useGetBiblePassageAnalysisMutation();

	const fireSavePassage = useAnalyticsEvent('Generate_Setlist_Save_Passage');

	let biblePassageErrorMessage = 'Could not validate passage — please try again';
	if (isBiblePassageError && biblePassageError instanceof FetchError) {
		biblePassageErrorMessage = biblePassageError.message;
	}

	const handleSave = async () => {
		try {
			await getBiblePassage({
				reference: bibleReference,
			});
			fireSavePassage();
			onSave(bibleReference);
		} catch (err) {
			console.log(err);
		}
	};

	const handleClear = () => {
		onSave(undefined);
	};

	return (
		<BottomCard
			title="Passage"
			hasCloseButton={true}
			closeButtonLabel="Cancel"
			onClose={onClose}
		>
			<BottomCardForm>
				<div style={{ position: 'relative' }}>
					<Label
						withOffset
						tooltipHeading="Bible passage"
						tooltip="Match your post-sermon song to the primary passage planned for the sermon. Enter the passage using standard bible citation **Book chapter:verses** (e.g. John 3:16-21)."
					>
						Bible Passage (optional)
					</Label>
					<BiblePassageInput
						placeholder="Genesis 1:1-10"
						name="bibleReference"
						id="bibleReference"
						value={bibleReference}
						onChange={(value) => {
							resetBiblePassage();
							setBibleReference(value);
						}}
						onError={resetBiblePassage}
						error={isBiblePassageError}
					/>
					{isBiblePassageError && (
						<FormHelperText error={true}>
							{biblePassageErrorMessage}
						</FormHelperText>
					)}
				</div>
			</BottomCardForm>
			<BottomCardAction>
				{initialBibleReference && (
					<AsaphButton
						onClick={handleClear}
						fullWidth
						label="Clear passage"
						color="grey"
					/>
				)}
				<AsaphButton
					isLoading={isBiblePassageLoading}
					onClick={handleSave}
					fullWidth
					label="Save"
					color="blue"
				/>
			</BottomCardAction>
		</BottomCard>
	);
};
